import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  FiChevronDown, FiPlus, FiTrash2, FiEdit2,
  FiEye, FiType, FiHash, FiCalendar,
  FiUploadCloud, FiCamera, FiCheckSquare, FiList,
  FiAlignLeft, FiArrowLeft, FiCopy, FiSearch, FiMenu,
} from "react-icons/fi";
import { HiCheck, HiX } from "react-icons/hi";

// ─── Constants ────────────────────────────────────────────────────────────────

const FORM_TYPES = [
  { id: 1,  name: "Payment" },       { id: 2,  name: "Warranty" },
  { id: 3,  name: "KYC" },           { id: 4,  name: "Login" },
  { id: 5,  name: "Registration" },  { id: 6,  name: "Profile" },
  { id: 7,  name: "Panel-AddUser" }, { id: 8,  name: "App-AddUser" },
  { id: 9,  name: "Enquiry" },       { id: 10, name: "WheelCampaign" },
  { id: 11, name: "App-Feedback" },  { id: 12, name: "Report-Qr" },
];

const LANGUAGES = [
  "english","mandarin","hindi","spanish","french",
  "arabic","bengali","portuguese","russian","urdu","indonesian","german",
];

const FIELD_TYPES = [
  { value: "text",     label: "Text",     icon: FiType },
  { value: "number",   label: "Number",   icon: FiHash },
  { value: "date",     label: "Date",     icon: FiCalendar },
  { value: "file",     label: "File",     icon: FiUploadCloud },
  { value: "camera",   label: "Camera",   icon: FiCamera },
  { value: "checkbox", label: "Checkbox", icon: FiCheckSquare },
  { value: "select",   label: "Select",   icon: FiList },
  { value: "textarea", label: "Textarea", icon: FiAlignLeft },
];

const MAPPING_KEYS = [
  { value: "state",       label: "State" },
  { value: "city",        label: "City" },
  { value: "pincode",     label: "Pincode" }
];

const TYPE_META = {
  text:     { icon: FiType,        label: "Text",     color: "bg-blue-50 text-blue-700 border-blue-200" },
  number:   { icon: FiHash,        label: "Number",   color: "bg-violet-50 text-violet-700 border-violet-200" },
  date:     { icon: FiCalendar,    label: "Date",     color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  file:     { icon: FiUploadCloud, label: "File",     color: "bg-amber-50 text-amber-700 border-amber-200" },
  camera:   { icon: FiCamera,      label: "Camera",   color: "bg-pink-50 text-pink-700 border-pink-200" },
  checkbox: { icon: FiCheckSquare, label: "Checkbox", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  select:   { icon: FiList,        label: "Select",   color: "bg-orange-50 text-orange-700 border-orange-200" },
  textarea: { icon: FiAlignLeft,   label: "Textarea", color: "bg-teal-50 text-teal-700 border-teal-200" },
};

// ─── Style tokens ─────────────────────────────────────────────────────────────

const inputCls =
  "block w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all";

const selectCls =
  "block w-full pl-3 pr-9 py-2.5 leading-5 border border-gray-300 rounded-lg bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all appearance-none cursor-pointer";

const labelCls = "block text-sm font-medium text-gray-700 mb-1";

// ─── Shared UI primitives ─────────────────────────────────────────────────────

const SelectField = ({ value, onChange, children, disabled }) => (
  <div className="relative">
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`${selectCls} ${disabled ? "opacity-60 cursor-not-allowed bg-gray-50" : ""}`}
    >
      {children}
    </select>
    <FiChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
  </div>
);

const Toggle = ({ checked, onChange, label }) => (
  <div className="flex items-center gap-3">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors focus:outline-none ${checked ? "bg-blue-600" : "bg-gray-200"}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${checked ? "translate-x-5" : "translate-x-0.5"}`}
      />
    </button>
    <span className="text-sm font-medium text-gray-700">{label}</span>
  </div>
);

const SectionCard = ({ title, desc, children }) => (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
    {(title || desc) && (
      <div className="mb-4 pb-3 border-b border-gray-100">
        {title && <h2 className="text-base font-semibold text-gray-900">{title}</h2>}
        {desc && <p className="text-sm text-gray-500 mt-0.5">{desc}</p>}
      </div>
    )}
    {children}
  </div>
);

// ─── Field preview (inside preview modal) ────────────────────────────────────

const FieldPreview = ({ field }) => {
  const sharedLabel = (
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {field.label}
      {field.required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );

  if (field.type === "select") return (
    <div>
      {sharedLabel}
      <SelectField value="" onChange={() => {}} disabled>
        <option value="">Select {field.label}…</option>
        {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </SelectField>
    </div>
  );

  if (field.type === "checkbox") return (
    <div className="flex items-center gap-2">
      <input type="checkbox" disabled className="rounded border-gray-300 h-4 w-4" />
      <label className="text-sm font-medium text-gray-700">
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>
    </div>
  );

  if (field.type === "textarea") return (
    <div>
      {sharedLabel}
      <textarea
        className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm resize-none text-gray-400"
        rows={2} disabled placeholder={`Enter ${field.label}`}
      />
    </div>
  );

  if (field.type === "file" || field.type === "camera") return (
    <div>
      {sharedLabel}
      <div className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-xl px-3 py-5 bg-gray-50">
        <FiUploadCloud className="h-5 w-5 text-gray-300" />
        <span className="text-xs text-gray-400">Click to upload</span>
      </div>
    </div>
  );

  return (
    <div>
      {sharedLabel}
      <input
        type={field.type === "date" ? "date" : field.type === "number" ? "number" : "text"}
        disabled
        placeholder={`Enter ${field.label}`}
        className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-400 cursor-not-allowed"
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

export default function AddForm() {
  const navigate = useNavigate();
  const location = useLocation();

  // Update mode
  const [isUpdate, setIsUpdate] = useState(false);
  const [formId,   setFormId]   = useState(null);

  // Form metadata
  const [templateName,    setTemplateName]    = useState("");
  const [formType,        setFormType]        = useState(0);
  const [language,        setLanguage]        = useState("english");
  const [companyIndustry, setCompanyIndustry] = useState({ industry_id: null, name: null });
  const [industryList,    setIndustryList]    = useState([]);

  // Fields list
  const [elements, setElements] = useState([]);

  // Field editor state (null = adding new)
  const [editingIdx,       setEditingIdx]       = useState(null);
  const [fieldName,        setFieldName]        = useState("");
  const [fieldLabel,       setFieldLabel]       = useState("");
  const [fieldType,        setFieldType]        = useState("text");
  const [fieldMaxLength,   setFieldMaxLength]   = useState("100");
  const [fieldRequired,    setFieldRequired]    = useState(false);
  const [fieldOptions,     setFieldOptions]     = useState([]);
  const [optionInput,      setOptionInput]      = useState("");
  const [fieldMapping,     setFieldMapping]     = useState(false);
  const [fieldMappingKey,  setFieldMappingKey]  = useState("");

  // Preview modal
  const [showPreview,        setShowPreview]        = useState(false);
  const [showInlinePreview,  setShowInlinePreview]  = useState(false);

  // Copy-from modal
  const [showCopyFrom,      setShowCopyFrom]      = useState(false);
  const [copyFromTemplates, setCopyFromTemplates] = useState([]);
  const [copyFromLoading,   setCopyFromLoading]   = useState(false);
  const [copyFromSelected,  setCopyFromSelected]  = useState(null);
  const [copyFromSearch,    setCopyFromSearch]    = useState("");

  // ── Initialise from location state (edit mode) ───────────────────────────
  useEffect(() => {
    if (location.state) {
      const s = location.state;
      setIsUpdate(true);
      setFormId(s.id);
      setTemplateName(s.template_name);
      setFormType(Number(s.form_type));
      setLanguage(s.language || "english");
      setElements(Object.values(s.template || {}));
      setCompanyIndustry({ industry_id: s.industry_id, name: s.industry_name });
    }
  }, [location.state]);

  // ── Load industry list ────────────────────────────────────────────────────
  useEffect(() => {
    axios({
      method: "get",
      url: `${process.env.REACT_APP_BASE_URL}/admin/industry`,
      headers: { Authorization: "Bearer " + sessionStorage.getItem("access_token") },
    })
      .then(r => setIndustryList(r.data.body || []))
      .catch(console.error);
  }, []);

  // ── Editor helpers ────────────────────────────────────────────────────────
  const resetEditor = () => {
    setEditingIdx(null);
    setFieldName(""); setFieldLabel(""); setFieldType("text");
    setFieldMaxLength("100"); setFieldRequired(false);
    setFieldOptions([]); setOptionInput("");
    setFieldMapping(false); setFieldMappingKey("");
  };

  const loadFieldForEdit = (idx) => {
    const f = elements[idx];
    setEditingIdx(idx);
    setFieldName(f.name);
    setFieldLabel(f.label);
    setFieldType(f.type);
    setFieldMaxLength(String(f.maxLength || "100"));
    setFieldRequired(!!f.required);
    setFieldOptions([...(f.options || [])]);
    setOptionInput("");
    setFieldMapping(!!f.mapping);
    setFieldMappingKey(f.mappingKey || "");
  };

  const handleAddOrUpdateField = () => {
    if (!fieldName.trim() || !fieldLabel.trim() || !fieldMaxLength) {
      toast.error("Fill all field properties before adding.");
      return;
    }
    if (fieldMapping && !fieldMappingKey.trim()) {
      toast.error("Mapping key is required when mapping is enabled.");
      return;
    }
    const trimmedName = fieldName.trim();
    const isDup = elements.some((el, i) => el.name === trimmedName && i !== editingIdx);
    if (isDup) {
      toast.error(`A field named "${trimmedName}" already exists.`);
      return;
    }
    const newField = {
      name:       trimmedName,
      label:      fieldLabel.trim(),
      type:       fieldType,
      required:   fieldRequired,
      maxLength:  fieldMaxLength,
      options:    fieldType === "select" ? [...fieldOptions] : [],
      mapping:    fieldMapping,
      mappingKey: fieldMapping ? fieldMappingKey.trim() : "",
    };
    if (editingIdx !== null) {
      setElements(prev => { const a = [...prev]; a[editingIdx] = newField; return a; });
      toast.success("Field updated");
    } else {
      setElements(prev => [...prev, newField]);
      toast.success("Field added");
    }
    resetEditor();
  };

  const handleDeleteField = (idx) => {
    setElements(prev => prev.filter((_, i) => i !== idx));
    if (editingIdx === idx) resetEditor();
  };

  // Moves a field from one index to another and keeps the open editor on the same field.
  const moveFieldTo = (from, to) => {
    if (from === to || to < 0 || to >= elements.length) return;
    const arr = [...elements];
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    setElements(arr);
    if (editingIdx === null) return;
    if (editingIdx === from) setEditingIdx(to);
    else if (from < editingIdx && to >= editingIdx) setEditingIdx(editingIdx - 1);
    else if (from > editingIdx && to <= editingIdx) setEditingIdx(editingIdx + 1);
  };

  const moveField = (idx, dir) => moveFieldTo(idx, idx + dir);

  // ── Drag-to-reorder (pointer events: mouse, touch and pen) ────────────────
  const itemRefs = useRef([]);
  const dragRef  = useRef(null);          // { from, over } — read inside pointer handlers
  const [drag, setDrag] = useState(null); // same shape, drives the drop indicator

  const updateDrag = (next) => {
    dragRef.current = next;
    setDrag(next);
  };

  const getDropIndex = (clientY) => {
    for (let i = 0; i < elements.length; i++) {
      const el = itemRefs.current[i];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (clientY < rect.top + rect.height / 2) return i;
    }
    return elements.length;
  };

  const handleDragStart = (e, idx) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    // Measure the row so the floating copy matches its size and stays under the grab point
    const rect = itemRefs.current[idx]?.getBoundingClientRect();
    updateDrag({
      from: idx,
      over: idx,
      field: elements[idx],
      x: e.clientX,
      y: e.clientY,
      offsetX: rect ? e.clientX - rect.left : 0,
      offsetY: rect ? e.clientY - rect.top : 0,
      width: rect ? rect.width : 0,
    });
  };

  const handleDragMove = (e) => {
    if (!dragRef.current) return;
    const y = e.clientY;
    // Auto-scroll when the pointer is near the top or bottom of the viewport
    const EDGE = 80;
    if (y < EDGE) window.scrollBy(0, -12);
    else if (y > window.innerHeight - EDGE) window.scrollBy(0, 12);

    const over = getDropIndex(y);
    updateDrag({ ...dragRef.current, x: e.clientX, y, over });
  };

  const handleDragEnd = () => {
    const current = dragRef.current;
    if (!current) return;
    // `over` is an insertion slot; once the dragged item is removed, slots after it shift up by one
    const target = current.over > current.from ? current.over - 1 : current.over;
    moveFieldTo(current.from, target);
    updateDrag(null);
  };

  const handleHandleKeyDown = (e, idx) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      moveField(idx, e.key === "ArrowUp" ? -1 : 1);
    }
  };

  const addOption = () => {
    const v = optionInput.trim();
    if (!v || fieldOptions.includes(v)) return;
    setFieldOptions(prev => [...prev, v]);
    setOptionInput("");
  };

  // ── Copy-from helpers ─────────────────────────────────────────────────────
  const openCopyFrom = () => {
    setShowCopyFrom(true);
    setCopyFromSelected(null);
    setCopyFromSearch("");
    if (copyFromTemplates.length > 0) return;
    setCopyFromLoading(true);
    axios({
      method: "get",
      url: `${process.env.REACT_APP_BASE_URL}/admin/formtemplate`,
      headers: { Authorization: "Bearer " + sessionStorage.getItem("access_token") },
    })
      .then(r => setCopyFromTemplates(r.data.body || []))
      .catch(() => toast.error("Could not load templates."))
      .finally(() => setCopyFromLoading(false));
  };

  const applyCopyFrom = (mode) => {
    if (!copyFromSelected) return;
    const fields = Object.values(copyFromSelected.template || {});
    if (mode === "replace") {
      setElements(fields);
      toast.success(`Replaced with ${fields.length} fields from "${copyFromSelected.template_name}"`);
    } else {
      setElements(prev => {
        const existingNames = new Set(prev.map(el => el.name));
        const toAdd    = fields.filter(f => !existingNames.has(f.name));
        const skipped  = fields.length - toAdd.length;
        if (skipped > 0) {
          toast.error(`${skipped} field${skipped > 1 ? "s" : ""} skipped — name already exists.`);
        }
        if (toAdd.length > 0) {
          toast.success(`Appended ${toAdd.length} fields from "${copyFromSelected.template_name}"`);
        }
        return [...prev, ...toAdd];
      });
    }
    setShowCopyFrom(false);
    setCopyFromSelected(null);
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    if (!templateName.trim()) { toast.error("Template name is required"); return false; }
    if (!isUpdate) {
      if (!companyIndustry.industry_id) { toast.error("Select an industry"); return false; }
      if (!formType || formType === 0)  { toast.error("Select a form type"); return false; }
      if (!language)                    { toast.error("Select a language");   return false; }
    }
    if (elements.length === 0) { toast.error("Add at least one field"); return false; }
    return true;
  };

  // ── Save / Update ─────────────────────────────────────────────────────────
  const handleSave = () => {
    if (!validate()) return;
    axios({
      method: "post",
      url: `${process.env.REACT_APP_BASE_URL}/admin/formtemplate/add`,
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + sessionStorage.getItem("access_token"),
      },
      data: JSON.stringify({
        template_name: templateName,
        form_type:     formType,
        industry_id:   Number(companyIndustry.industry_id),
        industry_name: companyIndustry.name,
        language,
        template:      { ...elements },
      }),
    })
      .then(() => {
        toast.success("Template Added Successfully", { id: "templateAddedSuccess" });
        setTimeout(() => navigate("/formTemplate"), 300);
      })
      .catch(() => toast.error("Some error occurred.", { id: "templateAddError" }));
  };

  const handleUpdate = () => {
    if (!validate()) return;
    axios({
      method: "put",
      url: `${process.env.REACT_APP_BASE_URL}/admin/formtemplate/${formId}`,
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + sessionStorage.getItem("access_token"),
      },
      data: JSON.stringify({
        template_name: templateName,
        template:      { ...elements },
      }),
    })
      .then(() => {
        toast.success("Template Updated Successfully", { id: "templateUpdatedSuccess" });
        setTimeout(() => navigate("/formTemplate"), 300);
      })
      .catch(() => toast.error("Some error occurred.", { id: "templateUpdateError" }));
  };

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="page-content">
      <div className="w-full space-y-4">

        {/* ── Page header ──────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button
              onClick={() => navigate("/formTemplate")}
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 transition-colors"
            >
              <FiArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 truncate">
                {templateName || (isUpdate ? "Edit Form" : "New Form")}
              </h1>
              <p className="text-sm text-gray-400">{isUpdate ? "Update form template" : "Create a new form template"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setShowPreview(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all"
            >
              <FiEye className="h-4 w-4" /> Preview
            </button>
            <button
              onClick={isUpdate ? handleUpdate : handleSave}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm transition-all"
            >
              <HiCheck className="h-4 w-4" />
              {isUpdate ? "Update Form" : "Save Form"}
            </button>
          </div>
        </div>

        {/* ── Two-panel builder ─────────────────────────────────────────────── */}
        <div className="flex flex-col lg:flex-row gap-4 items-start">

          {/* LEFT: field editor + form settings */}
          <div className="w-full lg:w-[400px] flex-shrink-0 flex flex-col gap-4">
            <SectionCard
              title={editingIdx !== null ? "Edit Field" : "Add Field"}
              desc={editingIdx !== null
                ? `Editing field #${editingIdx + 1}`
                : "Configure a new field, then click Add Field."}
            >
              <div className="space-y-3">

                <div>
                  <label className={labelCls}>Field Name <span className="text-red-500">*</span></label>
                  <input
                    className={inputCls}
                    placeholder="e.g. mobile, gstin, city"
                    value={fieldName}
                    onChange={e => setFieldName(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-gray-400">Snake_case data key — no spaces.</p>
                </div>

                <div>
                  <label className={labelCls}>Label <span className="text-red-500">*</span></label>
                  <input
                    className={inputCls}
                    placeholder="e.g. Mobile Number"
                    value={fieldLabel}
                    onChange={e => setFieldLabel(e.target.value)}
                  />
                </div>

                {/* Type · Max Length · Required — one row */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className={labelCls}>Type <span className="text-red-500">*</span></label>
                    <SelectField value={fieldType} onChange={e => setFieldType(e.target.value)}>
                      {FIELD_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </SelectField>
                  </div>
                  <div>
                    <label className={labelCls}>Max Length <span className="text-red-500">*</span></label>
                    <input
                      className={inputCls}
                      type="number"
                      min="1"
                      placeholder="100"
                      value={fieldMaxLength}
                      onChange={e => setFieldMaxLength(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={`${labelCls} invisible`}>Required</label>
                    <div className="flex items-center h-[38px]">
                      <Toggle checked={fieldRequired} onChange={setFieldRequired} label="Required" />
                    </div>
                  </div>
                </div>

                {/* Mapping toggle and mapping key — optional */}
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex items-center h-[38px]">
                    <Toggle checked={fieldMapping} onChange={setFieldMapping} label="Map to(Optional)" />
                  </div>
                  {fieldMapping && (
                    <div className="mt-2">
                      <label className={labelCls}>User Data Type <span className="text-red-500">*</span></label>
                      <SelectField value={fieldMappingKey} onChange={e => setFieldMappingKey(e.target.value)}>
                        <option value="">Select mapping type</option>
                        {MAPPING_KEYS.map(m => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </SelectField>
                      <p className="mt-1 text-xs text-gray-400">Map this field on basis of type (state, city, pincode, etc.)</p>
                    </div>
                  )}
                </div>

                {/* Options — only shown for select type */}
                {fieldType === "select" && (
                  <div>
                    <label className={labelCls}>Options</label>
                    {fieldOptions.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2.5">
                        {fieldOptions.map(opt => (
                          <span
                            key={opt}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-medium"
                          >
                            {opt}
                            <button
                              type="button"
                              onClick={() => setFieldOptions(prev => prev.filter(o => o !== opt))}
                              className="leading-none hover:text-blue-900 font-bold"
                            >×</button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input
                        className={inputCls}
                        placeholder="Type option, press Enter"
                        value={optionInput}
                        onChange={e => setOptionInput(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addOption(); } }}
                      />
                      <button
                        type="button"
                        onClick={addOption}
                        className="flex-shrink-0 px-3 py-2.5 rounded-lg bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 text-sm font-medium transition-colors"
                      >Add</button>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={handleAddOrUpdateField}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-all"
                  >
                    {editingIdx !== null
                      ? <><HiCheck className="h-4 w-4" /> Update Field</>
                      : <><FiPlus className="h-4 w-4" /> Add Field</>}
                  </button>
                  {editingIdx !== null && (
                    <button
                      type="button"
                      onClick={resetEditor}
                      className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-600 text-sm font-medium hover:bg-gray-50 transition-all"
                    >Cancel</button>
                  )}
                </div>

              </div>
            </SectionCard>

            {/* Form Settings — left column, below editor */}
            <SectionCard
              title="Form Settings"
              desc={isUpdate
                ? "Industry, type and language are locked after creation."
                : "Configure the form's industry, type and language."}
            >
              <div className="space-y-3">
                <div>
                  <label className={labelCls}>Template Name <span className="text-red-500">*</span></label>
                  <input
                    className={`${inputCls} ${isUpdate ? "opacity-60 cursor-not-allowed bg-gray-50" : ""}`}
                    placeholder="Template Name"
                    value={templateName}
                    onChange={e => setTemplateName(e.target.value)}
                    disabled={isUpdate}
                  />
                </div>
                <div>
                  <label className={labelCls}>Industry {!isUpdate && <span className="text-red-500">*</span>}</label>
                  <SelectField
                    value={companyIndustry.industry_id || 0}
                    disabled={isUpdate}
                    onChange={e => {
                      const ind = industryList.find(i => String(i.industry_id) === String(e.target.value));
                      setCompanyIndustry({ industry_id: e.target.value, name: ind?.name || null });
                    }}
                  >
                    <option value={0}>Select Industry</option>
                    {industryList.map(ind => (
                      <option key={ind.industry_id} value={ind.industry_id}>{ind.name}</option>
                    ))}
                  </SelectField>
                </div>
                {/* Form Type + Language — one row */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={labelCls}>Form Type {!isUpdate && <span className="text-red-500">*</span>}</label>
                    <SelectField value={formType} disabled={isUpdate} onChange={e => setFormType(Number(e.target.value))}>
                      <option value={0}>Select Type</option>
                      {FORM_TYPES.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </SelectField>
                  </div>
                  <div>
                    <label className={labelCls}>Language {!isUpdate && <span className="text-red-500">*</span>}</label>
                    <SelectField value={language} disabled={isUpdate} onChange={e => setLanguage(e.target.value)}>
                      <option value="">Select Language</option>
                      {LANGUAGES.map(l => (
                        <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>
                      ))}
                    </SelectField>
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>

          {/* RIGHT: field list */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Form Fields</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {elements.length} field{elements.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={openCopyFrom}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-xs font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all"
                >
                  <FiCopy className="h-3.5 w-3.5" /> Copy from
                </button>
              </div>

              {elements.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-2 text-center">
                  <FiList className="h-12 w-12 text-gray-200" />
                  <p className="text-sm font-semibold text-gray-400">No fields yet</p>
                  <p className="text-xs text-gray-300">Use the editor on the left to add fields.</p>
                </div>
              ) : (
                <div className="space-y-1.5 relative">
                  {elements.map((field, idx) => {
                    const meta    = TYPE_META[field.type] || { icon: FiType, label: field.type, color: "bg-gray-100 text-gray-600 border-gray-200" };
                    const Icon    = meta.icon;
                    const editing = editingIdx === idx;
                    const isDragging = drag?.from === idx;
                    const lastIdx    = elements.length - 1;
                    // Hide the indicator when the drop would not move the item
                    const dropActive = drag && drag.over !== drag.from && drag.over !== drag.from + 1;
                    const showTopLine    = dropActive && drag.over === idx;
                    const showBottomLine = dropActive && drag.over === elements.length && idx === lastIdx;

                    return (
                      <div
                        key={idx}
                        ref={el => { itemRefs.current[idx] = el; }}
                        className={`relative flex items-center gap-2 rounded-xl border px-2.5 py-2 transition-all ${
                          isDragging
                            ? "border-2 border-dashed border-blue-400 bg-blue-50/60 [&>*]:invisible"
                            : editing
                              ? "border-blue-500 ring-2 ring-blue-100 bg-blue-50/30"
                              : "border-gray-200 bg-white hover:border-gray-300"
                        }`}
                      >
                        {showTopLine && (
                          <div className="pointer-events-none absolute -top-1.5 left-0 right-0 h-1 rounded-full bg-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.15)]" />
                        )}
                        {showBottomLine && (
                          <div className="pointer-events-none absolute -bottom-1.5 left-0 right-0 h-1 rounded-full bg-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.15)]" />
                        )}

                        {/* Drag handle + index */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            type="button"
                            aria-label={`Reorder ${field.label || "field"} — drag, or use arrow keys`}
                            title="Drag to reorder (↑/↓ keys also work)"
                            onPointerDown={e => handleDragStart(e, idx)}
                            onPointerMove={handleDragMove}
                            onPointerUp={handleDragEnd}
                            onPointerCancel={handleDragEnd}
                            onKeyDown={e => handleHandleKeyDown(e, idx)}
                            className="flex h-7 w-5 items-center justify-center rounded text-gray-300 hover:text-gray-600 hover:bg-gray-100 cursor-grab active:cursor-grabbing touch-none select-none transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
                          >
                            <FiMenu className="h-3.5 w-3.5" />
                          </button>
                          <span className="w-4 text-center text-[10px] text-gray-400 font-mono leading-none">{idx + 1}</span>
                        </div>

                        {/* Type chip */}
                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${meta.color}`}>
                          <Icon className="h-2.5 w-2.5" /> {meta.label}
                        </span>

                        {/* Label + key */}
                        <div className="flex-1 min-w-0 flex items-baseline gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-800 truncate">{field.label}</span>
                          <span className="text-[11px] text-gray-500 font-mono flex-shrink-0">{field.name}</span>
                          <span className="text-[11px] text-gray-500 flex-shrink-0">max {field.maxLength}</span>
                          {field.type === "select" && (field.options || []).length > 0 && (
                            <span className="text-[11px] text-gray-400 flex-shrink-0 truncate max-w-[120px]">
                              [{field.options.join(", ")}]
                            </span>
                          )}
                          {field.mapping && field.mappingKey && (
                            <span className="text-[11px] text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-200 flex-shrink-0">
                              maps→{MAPPING_KEYS.find(m => m.value === field.mappingKey)?.label || field.mappingKey}
                            </span>
                          )}
                        </div>

                        {/* Required badge */}
                        {field.required && (
                          <span className="inline-flex items-center text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-red-50 text-red-500 border border-red-200 flex-shrink-0">
                            Req
                          </span>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => editing ? resetEditor() : loadFieldForEdit(idx)}
                            className={`flex h-7 w-7 items-center justify-center rounded-lg border transition-colors ${
                              editing
                                ? "border-blue-300 bg-blue-100 text-blue-700 hover:bg-blue-200"
                                : "border-gray-200 text-gray-400 hover:border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {editing ? <HiCheck className="h-3 w-3" /> : <FiEdit2 className="h-3 w-3" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteField(idx)}
                            className="flex h-7 w-7 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-400 hover:bg-red-100 transition-colors"
                          >
                            <FiTrash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Floating copy of the dragged row, follows the pointer */}
                  {drag?.field && (() => {
                    const meta = TYPE_META[drag.field.type] || { icon: FiType, label: drag.field.type, color: "bg-gray-100 text-gray-600 border-gray-200" };
                    const Icon = meta.icon;
                    return (
                      <div
                        className="pointer-events-none fixed z-50 flex items-center gap-2 rounded-xl border border-blue-400 bg-white px-2.5 py-2 shadow-2xl ring-4 ring-blue-500/10 rotate-[1.5deg]"
                        style={{
                          left: drag.x - drag.offsetX,
                          top: drag.y - drag.offsetY,
                          width: drag.width,
                        }}
                      >
                        <FiMenu className="h-3.5 w-3.5 flex-shrink-0 text-blue-500" />
                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${meta.color}`}>
                          <Icon className="h-2.5 w-2.5" /> {meta.label}
                        </span>
                        <span className="text-sm font-semibold text-gray-800 truncate">{drag.field.label}</span>
                        <span className="text-[11px] text-gray-500 font-mono truncate">{drag.field.name}</span>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* ── Inline preview toggle ──────────────────────────────────── */}
              {elements.length > 0 && (
                <div className="mt-4 border-t border-gray-100 pt-3">
                  <button
                    type="button"
                    onClick={() => setShowInlinePreview(p => !p)}
                    className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    <FiEye className="h-4 w-4" />
                    {showInlinePreview ? "Hide form preview" : "Show form preview"}
                    <FiChevronDown className={`h-4 w-4 transition-transform ${showInlinePreview ? "rotate-180" : ""}`} />
                  </button>

                  {showInlinePreview && (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                      {elements.map((field, idx) => (
                        <FieldPreview key={idx} field={field} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* ── Preview Modal ──────────────────────────────────────────────────── */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowPreview(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{templateName || "Form Preview"}</h3>
                <p className="text-xs text-gray-400 mt-0.5">{elements.length} field{elements.length !== 1 ? "s" : ""}</p>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
              >
                <HiX className="h-4 w-4" />
              </button>
            </div>
            <div className="overflow-auto flex-1 p-4">
              {elements.length === 0 ? (
                <p className="text-center text-sm text-gray-400 py-8">No fields to preview yet.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {elements.map((field, idx) => (
                    <FieldPreview key={idx} field={field} />
                  ))}
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-100 flex justify-end flex-shrink-0">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Copy-from Modal ───────────────────────────────────────────────── */}
      {showCopyFrom && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowCopyFrom(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden">

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
              <div>
                <h3 className="text-base font-bold text-gray-900">Copy from Template</h3>
                <p className="text-xs text-gray-400 mt-0.5">Pick a template to import its fields into this form.</p>
              </div>
              <button
                onClick={() => setShowCopyFrom(false)}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
              >
                <HiX className="h-4 w-4" />
              </button>
            </div>

            <div className="flex flex-1 min-h-0 overflow-hidden">

              {/* LEFT: template list */}
              <div className="w-56 flex-shrink-0 border-r border-gray-100 flex flex-col">
                <div className="p-3 border-b border-gray-100">
                  <div className="relative">
                    <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search…"
                      value={copyFromSearch}
                      onChange={e => setCopyFromSearch(e.target.value)}
                      className="block w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all"
                    />
                  </div>
                </div>
                <div className="overflow-auto flex-1">
                  {copyFromLoading ? (
                    <div className="flex items-center justify-center py-10">
                      <div className="h-5 w-5 rounded-full border-2 border-blue-200 border-t-blue-600 animate-spin" />
                    </div>
                  ) : copyFromTemplates.length === 0 ? (
                    <p className="text-xs text-center text-gray-400 py-8">No templates found.</p>
                  ) : (
                    copyFromTemplates
                      .filter(t => t.template_name?.toLowerCase().includes(copyFromSearch.toLowerCase()))
                      .map(t => {
                        const fieldCount = Object.keys(t.template || {}).length;
                        const selected   = copyFromSelected?.id === t.id;
                        return (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => setCopyFromSelected(t)}
                            className={`w-full text-left px-3 py-2.5 border-b border-gray-50 transition-colors ${
                              selected
                                ? "bg-blue-50 border-l-2 border-l-blue-500"
                                : "hover:bg-gray-50"
                            }`}
                          >
                            <p className={`text-xs font-semibold truncate ${selected ? "text-blue-700" : "text-gray-800"}`}>
                              {t.template_name}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">{fieldCount} fields · {t.industry_name || "—"}</p>
                          </button>
                        );
                      })
                  )}
                </div>
              </div>

              {/* RIGHT: selected template fields preview */}
              <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                {!copyFromSelected ? (
                  <div className="flex flex-col items-center justify-center h-full gap-2 text-center p-6">
                    <FiCopy className="h-10 w-10 text-gray-200" />
                    <p className="text-sm font-medium text-gray-400">Select a template from the left</p>
                    <p className="text-xs text-gray-300">Its fields will appear here for preview.</p>
                  </div>
                ) : (
                  <>
                    <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
                      <p className="text-sm font-semibold text-gray-800">{copyFromSelected.template_name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {Object.keys(copyFromSelected.template || {}).length} fields · {copyFromSelected.language} · {copyFromSelected.industry_name}
                      </p>
                    </div>
                    <div className="overflow-auto flex-1 p-4">
                      <div className="space-y-1.5">
                        {Object.values(copyFromSelected.template || {}).map((field, idx) => {
                          const meta = TYPE_META[field.type] || { label: field.type, color: "bg-gray-100 text-gray-600 border-gray-200" };
                          return (
                            <div key={idx} className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-gray-100 bg-gray-50 flex-wrap">
                              <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${meta.color}`}>
                                {meta.label}
                              </span>
                              <span className="text-xs font-medium text-gray-700 truncate">{field.label}</span>
                              <span className="text-xs text-gray-400 font-mono ml-auto flex-shrink-0">{field.name}</span>
                              {field.required && (
                                <span className="text-xs text-red-400 flex-shrink-0">req</span>
                              )}
                              {field.mapping && field.mappingKey && (
                                <span className="text-xs text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded flex-shrink-0">
                                  maps→{MAPPING_KEYS.find(m => m.value === field.mappingKey)?.label || field.mappingKey}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Footer actions */}
            <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
              <p className="text-xs text-gray-400">
                {copyFromSelected
                  ? `${Object.keys(copyFromSelected.template || {}).length} fields will be copied`
                  : "No template selected"}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowCopyFrom(false)}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 text-xs font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!copyFromSelected}
                  onClick={() => applyCopyFrom("append")}
                  className="px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-xs font-semibold hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Append fields
                </button>
                <button
                  type="button"
                  disabled={!copyFromSelected}
                  onClick={() => applyCopyFrom("replace")}
                  className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Replace fields
                </button>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
